import { CPIFrequency, MetricPointState } from 'models'

const MS_PER_DAY = 24 * 60 * 60 * 1000

const toUtcMs = (dateISO: string) => {
  const [year, month, day] = dateISO.slice(0, 10).split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

export const utcDayDiff = (fromISO: string, toISO: string) => (toUtcMs(toISO) - toUtcMs(fromISO)) / MS_PER_DAY

export const addUtcDays = (dateISO: string, days: number): string =>
  new Date(toUtcMs(dateISO) + days * MS_PER_DAY).toISOString().slice(0, 10)

/* Monday-of-week (UTC) as YYYY-MM-DD, matching the weekStartDay=1 convention of groupByWeeks. */
export const getWeekStartUTC = (dateISO: string): string => {
  const date = new Date(toUtcMs(dateISO))
  const diff = (date.getUTCDay() + 6) % 7
  date.setUTCDate(date.getUTCDate() - diff)
  return date.toISOString().slice(0, 10)
}

/* Weekly buckets carry the first PRESENT day of the week, so compare week starts, not raw dates. */
export const isGapBetween = (prevDateISO: string, currDateISO: string, frequency: CPIFrequency): boolean => {
  switch (frequency) {
    case CPIFrequency.Weekly:
      return utcDayDiff(getWeekStartUTC(prevDateISO), getWeekStartUTC(currDateISO)) > 7
    case CPIFrequency.Monthly: {
      const [prevYear, prevMonth] = prevDateISO.split('-').map(Number)
      const [currYear, currMonth] = currDateISO.split('-').map(Number)
      return currYear * 12 + currMonth - (prevYear * 12 + prevMonth) > 1
    }
    default:
      return utcDayDiff(prevDateISO, currDateISO) > 1
  }
}

/* 'M' breaks the trend line at a missing interval; gapsEnabled guards non-Ready stub data (monthly-spaced). */
export const getLineCommand = ({
  isFirstPoint,
  prevDate,
  currDate,
  frequency,
  gapsEnabled
}: {
  isFirstPoint: boolean
  prevDate: string | null
  currDate: string
  frequency: CPIFrequency
  gapsEnabled: boolean
}): 'M' | 'L' => {
  if (isFirstPoint) return 'M'
  if (!gapsEnabled) return 'L'
  /* Every earlier stripe was empty, so there is no point to draw a line from. */
  if (!prevDate) return 'M'
  return isGapBetween(prevDate, currDate, frequency) ? 'M' : 'L'
}

export const pointStateTitleHtml = (pointState?: MetricPointState): string => {
  if (pointState === 'estimated') return `<p class="tooltip-state-title">Estimated Data Point</p>`
  if (pointState === 'partial') return `<p class="tooltip-state-title">Partially Estimated Data</p>`
  return ''
}

export const decorateMetricLabel = (label: string, pointState?: MetricPointState): string =>
  pointState === 'estimated' ? `Est. ${label}` : label
